import { assertNumericRecord, decodeNumericDutyInput, NUMERIC_DUTY_PROTOCOL_VERSION, NUMERIC_DUTY_RULES_VERSION, NumericDutyInputError, } from "./numeric.js";
import { GPU_DUTY_ALGORITHM_VERSION } from "./packed/shadow.js";
import { PACKED_UNASSIGNED, RULE_FLAG_AVOIDANCE_PATTERNS, RULE_FLAG_FORBIDDEN_PATTERNS, RULE_FLAG_PONGDANG_PATTERNS, SHIFT_FLAG_NIGHT, SHIFT_FLAG_OFF, SHIFT_PATTERN_MASK, SHIFT_PATTERN_SHIFT, } from "./packed/types.js";
/** Internal shared scanner. Call once per calendar day; unknown cells break the known sequence. */
export function createDutyRuleSequence(rules) {
    let day = -1, work = 0, night = 0, localWork = 0, localNight = 0, chunkStart = 0;
    const history = [];
    return (shiftFlags, boundary = false) => {
        day++;
        if (boundary) {
            chunkStart = day;
            localWork = localNight = 0;
        }
        const findings = [];
        const report = (code, crossesBoundary) => findings.push({ code, day, crossesBoundary });
        if (shiftFlags === undefined) {
            work = night = localWork = localNight = 0;
            history.push(-1);
        }
        else {
            work = (shiftFlags & SHIFT_FLAG_OFF) !== 0 ? 0 : work + 1;
            localWork = (shiftFlags & SHIFT_FLAG_OFF) !== 0 ? 0 : localWork + 1;
            night = (shiftFlags & SHIFT_FLAG_NIGHT) !== 0 ? night + 1 : 0;
            localNight = (shiftFlags & SHIFT_FLAG_NIGHT) !== 0 ? localNight + 1 : 0;
            if (work > rules[0])
                report("consecutive-work", localWork <= rules[0]);
            if (night > rules[1])
                report("consecutive-night", localNight <= rules[1]);
            const current = (shiftFlags & SHIFT_PATTERN_MASK) >>> SHIFT_PATTERN_SHIFT;
            const previous = history[history.length - 1], second = history[history.length - 2], third = history[history.length - 3];
            if ((rules[2] & RULE_FLAG_FORBIDDEN_PATTERNS) !== 0 &&
                ((previous === 3 && (current === 1 || current === 2)) || (previous === 2 && current === 1)))
                report("forbidden-pattern", day - 1 < chunkStart);
            if ((rules[2] & RULE_FLAG_AVOIDANCE_PATTERNS) !== 0 &&
                second === 3 &&
                ((previous === 3 && current === 3) || (previous === 4 && (current === 1 || current === 2))))
                report("avoidance-pattern", day - 2 < chunkStart);
            if ((rules[2] & RULE_FLAG_PONGDANG_PATTERNS) !== 0 &&
                third === 3 &&
                second === 4 &&
                previous === 3 &&
                current === 4)
                report("pongdang-pattern", day - 3 < chunkStart);
            history.push(current);
        }
        if (history.length > 3)
            history.shift();
        return findings;
    };
}
/** Added rule findings lost by independent chunk validation. Not a complete schedule validator. */
export function diagnoseDutyBoundaries(value) {
    assertNumericRecord(value, ["nurseCount", "dayCount", "assignments", "shiftFlags", "rules", "boundaryDays"], "boundary");
    const validInteger = (v, min, max) => Number.isSafeInteger(v) && v >= min && v <= max;
    if (!validInteger(value.nurseCount, 1, 50) ||
        !validInteger(value.dayCount, 1, 10000) ||
        !Array.isArray(value.shiftFlags) ||
        value.shiftFlags.length < 1 ||
        value.shiftFlags.length > 32 ||
        !Array.isArray(value.assignments) ||
        value.assignments.length !== value.nurseCount * value.dayCount ||
        !Array.isArray(value.boundaryDays) ||
        value.boundaryDays.length >= value.dayCount ||
        Array.from(value.boundaryDays).some((day, i, days) => !validInteger(day, 1, value.dayCount - 1) || (i > 0 && day <= days[i - 1])))
        throw new NumericDutyInputError("boundary.shape");
    // Reuse the wire validator for the exact shift/rule semantics, without changing its 31-day limit.
    const shifts = value.shiftFlags.length;
    const flags = decodeNumericDutyInput({
        protocolVersion: NUMERIC_DUTY_PROTOCOL_VERSION,
        engineVersion: GPU_DUTY_ALGORITHM_VERSION,
        rulesVersion: NUMERIC_DUTY_RULES_VERSION,
        nurseCount: 1,
        dayCount: 1,
        shiftCount: shifts,
        requirements: Array(shifts).fill(0),
        fixedAssignments: [PACKED_UNASSIGNED],
        availability: [1],
        availableShiftBits: [(2 ** shifts - 1) >>> 0],
        shiftFlags: value.shiftFlags,
        rules: value.rules,
    });
    for (const shift of value.assignments)
        if (shift !== PACKED_UNASSIGNED && !validInteger(shift, 0, shifts - 1))
            throw new NumericDutyInputError("boundary.assignment");
    const input = value;
    const boundaries = new Set(input.boundaryDays);
    const result = {
        totalIssueCount: 0,
        truncated: false,
        missingAssignmentCount: 0,
        issues: [],
    };
    for (let nurse = 0; nurse < input.nurseCount; nurse++) {
        const scan = createDutyRuleSequence(flags.rules);
        for (let day = 0; day < input.dayCount; day++) {
            const shift = input.assignments[day * input.nurseCount + nurse];
            if (shift === PACKED_UNASSIGNED)
                result.missingAssignmentCount++;
            for (const finding of scan(shift === PACKED_UNASSIGNED ? undefined : flags.shiftFlags[shift], boundaries.has(day))) {
                if (!finding.crossesBoundary)
                    continue;
                result.totalIssueCount++;
                if (result.issues.length < 128)
                    result.issues.push({ code: finding.code, day, nurse });
            }
        }
    }
    result.truncated = result.totalIssueCount > result.issues.length;
    return result;
}
//# sourceMappingURL=duty-boundaries.js.map